import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Clock } from "lucide-react";
import LinkManagement from "./LinkManagement";
import MessageManagement from "./MessageManagement";

interface Props {
  sessionToken: string;
}

const RegistrationControls = ({ sessionToken }: Props) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [openUntil, setOpenUntil] = useState("");
  const [nextOpenDate, setNextOpenDate] = useState("");
  const { toast } = useToast();

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "get_registration_settings" },
      });
      const s = res.data?.settings;
      if (s) {
        setRegistrationOpen(s.registration_open);
        setOpenUntil(s.open_until ? new Date(s.open_until).toISOString().slice(0, 16) : "");
        setNextOpenDate(s.next_open_date ? new Date(s.next_open_date).toISOString().slice(0, 16) : "");
      }
    } catch {
      toast({ title: "Failed to load settings", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: {
          action: "update_registration_settings",
          sessionToken,
          registration_open: registrationOpen,
          open_until: openUntil ? new Date(openUntil).toISOString() : null,
          next_open_date: nextOpenDate ? new Date(nextOpenDate).toISOString() : null,
        },
      });
      if (res.data?.error) throw new Error(res.data.error);
      toast({ title: "Settings saved!" });
    } catch (err) {
      const error = err as Error;
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Registration Timer Controls
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-3">
            <Switch
              checked={registrationOpen}
              onCheckedChange={setRegistrationOpen}
              id="reg-toggle"
            />
            <Label htmlFor="reg-toggle" className="text-base font-medium">
              Registration {registrationOpen ? "Open" : "Closed"}
            </Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="open-until">Registration Closes At</Label>
              <Input
                id="open-until"
                type="datetime-local"
                value={openUntil}
                onChange={(e) => setOpenUntil(e.target.value)}
                disabled={!registrationOpen}
              />
              <p className="text-xs text-muted-foreground">
                When registration is open, countdown shows time until this date
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="next-open">Next Registration Opens At</Label>
              <Input
                id="next-open"
                type="datetime-local"
                value={nextOpenDate}
                onChange={(e) => setNextOpenDate(e.target.value)}
                disabled={registrationOpen}
              />
              <p className="text-xs text-muted-foreground">
                When registration is closed, countdown shows time until this date
              </p>
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Settings
          </Button>
        </CardContent>
      </Card>

      <LinkManagement sessionToken={sessionToken} />
      <MessageManagement sessionToken={sessionToken} />
    </div>
  );
};

export default RegistrationControls;
